import React from "react";
import { Globe, Monitor } from "lucide-react";
import { useLiveSource } from "@/hooks/useLiveSource";

/**
 * Global Live / Preview data-source toggle for admin pages.
 * Reads + writes the shared `useLiveSource` hook so flipping anywhere in the
 * admin UI is reflected on every page that subscribes to it.
 *
 * Props are all optional and only kept for back-compat with older call sites
 * that passed local state — the hook is the source of truth either way.
 */
export function LiveToggle(props = {}) {
  const hook = useLiveSource();
  // Back-compat: old call sites passed `source / setSource / relayConfigured /
  // isPreview` directly. Prefer the hook when callers omit the props.
  const source = props.source ?? hook.source;
  const setSource = props.setSource ?? hook.setSource;
  const relayConfigured = props.relayConfigured ?? hook.relayConfigured;
  const isPreview = props.isPreview ?? hook.isPreview;

  // Don't show toggle on the live system — data is already live there.
  if (!isPreview) return null;

  return (
    <div
      className="inline-flex rounded-lg border border-border overflow-hidden text-xs"
      data-testid="live-toggle"
      title={
        !relayConfigured
          ? "Configure the relay (Admin → Integrations → Live Relay) to enable Live."
          : "Switch between preview-DB and live-production data (read-only)."
      }
    >
      <button
        type="button"
        onClick={() => setSource("preview")}
        className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
          source === "preview"
            ? "bg-slate-800 text-white"
            : "bg-transparent text-muted-foreground hover:bg-accent"
        }`}
        data-testid="toggle-preview"
      >
        <Monitor className="h-3 w-3" /> Preview
      </button>
      <button
        type="button"
        onClick={() => relayConfigured && setSource("live")}
        disabled={!relayConfigured}
        className={`px-3 py-1.5 flex items-center gap-1.5 transition-colors ${
          source === "live"
            ? "bg-emerald-600 text-white"
            : relayConfigured
            ? "bg-transparent text-muted-foreground hover:bg-accent"
            : "bg-transparent text-muted-foreground/40 cursor-not-allowed"
        }`}
        data-testid="toggle-live"
      >
        <Globe className="h-3 w-3" /> Live
        {!relayConfigured && <span className="text-[9px] opacity-60">(not connected)</span>}
      </button>
    </div>
  );
}
